/* istanbul ignore file */
import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Card } from "@app/components/shared/Card";
import { Button } from "@app/components/shared/Button";
import { Pill } from "@app/components/shared/Pill";
import {
  activateTradingAccount,
  deleteTradingAccount,
  registerTradingAccount,
  refreshAccountSnapshot,
  rotateTradingAccount,
  validateBybitKeys,
  getTelegramStatus,
  unlinkTelegram,
  type RegisterTradingAccountInput,
  type RotateTradingAccountInput,
  type ValidateBybitKeysResponse,
  type TelegramStatus,
} from "@app/services/integrationsApi";
import type {
  IntegrationsPayload,
  IntegrationStatus,
} from "@shared/types/integrations";
import { INTEGRATIONS_QUERY_KEY, useIntegrationsData } from "./hooks/useIntegrationsData";
import { BybitOnboardingModal } from "@app/components/onboarding/BybitOnboardingModal";
import { AdminBroadcastCard } from "./AdminBroadcastCard";
import { AdminEmailSettingsCard } from "./AdminEmailSettingsCard";
import { AdminScheduledReportsCard } from "./AdminScheduledReportsCard";
import { useAuth } from "@app/hooks/useAuth";
import "./integrations.css";

export function IntegrationsTab(): JSX.Element {
  const queryClient = useQueryClient();
  const { data, isLoading, isError, refetch } = useIntegrationsData();
  const { user: currentUser } = useAuth();
  const isAdmin = currentUser?.role === "admin";

  const { data: telegramStatus, refetch: refetchTelegramStatus } = useQuery<TelegramStatus>({
    queryKey: ["integrations", "telegram-status"],
    queryFn: getTelegramStatus,
    enabled: !isAdmin,
  });

  // Build a Telegram deep link carrying the user's account id, so the bot can
  // bind the Telegram chat to the correct account after the user presses /start.
  const buildTelegramDeepLink = (botLink: string | null | undefined): string | null => {
    if (!botLink) return botLink ?? null;
    if (!currentUser?.user_id) return botLink;
    const separator = botLink.includes("?") ? "&" : "?";
    return `${botLink}${separator}start=${encodeURIComponent(currentUser.user_id)}`;
  };

  const unlinkTelegramMutation = useMutation({
    mutationFn: unlinkTelegram,
    onSuccess: async () => {
      setNotice({ type: "success", message: "Telegram account unlinked successfully." });
      await refetchTelegramStatus();
    },
    onError: (error: unknown) => {
      setNotice({ type: "error", message: error instanceof Error ? error.message : "Failed to unlink Telegram." });
    },
  });

  const [notice, setNotice] = useState<{ type: "success" | "error"; message: string } | null>(null);
  const [isAccountFormOpen, setAccountFormOpen] = useState(false);
  const [formState, setFormState] = useState<RegisterTradingAccountInput>({
    provider: "",
    apiKey: "",
    apiSecret: "",
    nickname: "",
    owner: "",
    scopes: [],
    metadata: {},
  });
  const [formError, setFormError] = useState<string | null>(null);
  const [isSubmitting, setSubmitting] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [validationResult, setValidationResult] = useState<ValidateBybitKeysResponse | null>(null);
  const [autoValidationTimeout, setAutoValidationTimeout] = useState<ReturnType<typeof setTimeout> | null>(null);
  const [consentAccepted, setConsentAccepted] = useState(false);

  const [rotationAccountId, setRotationAccountId] = useState<string | null>(null);
  const [rotationState, setRotationState] = useState<RotateTradingAccountInput>({
    apiKey: "",
    apiSecret: "",
  });
  const [rotationError, setRotationError] = useState<string | null>(null);

  const [isOnboardingModalOpen, setIsOnboardingModalOpen] = useState(false);
  const [showStepGuide, setShowStepGuide] = useState<{
    step: 1 | 2 | 3 | null;
    message: string;
  } | null>(null);

  const integrationsData: IntegrationsPayload | undefined = data ?? undefined;
  const tradingAccounts = Array.isArray(integrationsData?.tradingAccounts?.items)
    ? integrationsData?.tradingAccounts.items ?? []
    : [];
  const pendingRequests = Array.isArray(integrationsData?.pendingRequests?.items)
    ? integrationsData?.pendingRequests.items ?? []
    : [];

  const registerMutation = useMutation({
    mutationFn: registerTradingAccount,
    onMutate: () => {
      setSubmitting(true);
      setFormError(null);
    },
    onSuccess: async () => {
      // Show step guide for next step (activation)
      setShowStepGuide({
        step: 2,
        message: "Great! Your account has been created. Now go to the 'Trading Accounts' section above and click 'Activate' to enable it.",
      });
      setNotice({ 
        type: "success", 
        message: "Account created successfully! Next step: Go to 'Trading Accounts' section above and click 'Activate'." 
      });
      await queryClient.invalidateQueries({ queryKey: INTEGRATIONS_QUERY_KEY });
      setFormState({
        provider: "",
        apiKey: "",
        apiSecret: "",
        nickname: "",
        owner: "",
        scopes: [],
        metadata: {},
      });
      setValidationResult(null);
      setIsValidating(false);
      setAccountFormOpen(false);
      setConsentAccepted(false);
    },
    onError: (error: unknown) => {
      setFormError(error instanceof Error ? error.message : "Failed to register account.");
    },
    onSettled: () => setSubmitting(false),
  });

  const activationMutation = useMutation({
    mutationFn: activateTradingAccount,
    onSuccess: async () => {
      setNotice({ 
        type: "success", 
        message: "Trading account activated successfully! Your account is now ready for trading." 
      });
      // Clear step guide after successful activation
      setShowStepGuide(null);
      await queryClient.invalidateQueries({ queryKey: INTEGRATIONS_QUERY_KEY });
    },
    onError: (error: unknown) => {
      setNotice({ type: "error", message: error instanceof Error ? error.message : "Failed to activate account." });
    },
  });

  const rotationMutation = useMutation({
    mutationFn: async () => {
      if (!rotationAccountId) {
        throw new Error("No account selected for rotation.");
      }
      return rotateTradingAccount(rotationAccountId, rotationState);
    },
    onMutate: () => {
      setRotationError(null);
    },
    onSuccess: async () => {
      setNotice({ type: "success", message: "Credentials rotated. Account pending re-validation." });
      // Show step guide for activation after rotation
      setShowStepGuide({
        step: 2,
        message: "API credentials updated! Click 'Activate' to re-validate and enable the account.",
      });
      await queryClient.invalidateQueries({ queryKey: INTEGRATIONS_QUERY_KEY });
      setRotationAccountId(null);
      setRotationState({ apiKey: "", apiSecret: "" });
    },
    onError: (error: unknown) => {
      setRotationError(error instanceof Error ? error.message : "Failed to rotate credentials.");
    },
  });

  const deleteAccountMutation = useMutation({
    mutationFn: deleteTradingAccount,
    onSuccess: async () => {
      setNotice({ type: "success", message: "Trading account removed." });
      await queryClient.invalidateQueries({ queryKey: INTEGRATIONS_QUERY_KEY });
    },
    onError: (error: unknown) => {
      setNotice({ type: "error", message: error instanceof Error ? error.message : "Failed to delete account." });
    },
  });

  useEffect(() => {
    if (isError) {
      setNotice({ type: "error", message: "Failed to load integrations. Please retry." });
    }
  }, [isError]);

  useEffect(() => {
    if (!notice) {
      return;
    }
    const timer = window.setTimeout(() => setNotice(null), 5000);
    return () => window.clearTimeout(timer);
  }, [notice]);

  useEffect(() => {
    if (!isLoading && tradingAccounts.length === 0 && !isAccountFormOpen) {
      const dontShow = localStorage.getItem("onboarding_dont_show");
      if (dontShow !== "true") {
        setIsOnboardingModalOpen(true);
      }
    }
  }, [isLoading, isAccountFormOpen, tradingAccounts.length]);

  const handleValidateBybit = async () => {
    if (!formState.provider || formState.provider !== "bybit") {
      setFormError("Validation is only available for Bybit accounts.");
      return;
    }
    if (!formState.apiKey || !formState.apiSecret) {
      setFormError("API key and secret are required for validation.");
      return;
    }

    setIsValidating(true);
    setFormError(null);
    setValidationResult(null);

    try {
      const result = await validateBybitKeys({
        api_key: formState.apiKey,
        api_secret: formState.apiSecret,
        testnet: false,
      });

      setValidationResult(result);

      if (!result.valid) {
        if (result.withdraw_enabled || result.deposit_enabled) {
          setFormError(
            "Disable Withdraw/Deposit permissions in Bybit API settings, then try again.",
          );
        } else {
          setFormError(result.error || "API keys validation failed.");
        }
      } else {
        setFormError(null);
        setNotice({
          type: "success",
          message: "API keys validated successfully. You can now save the integration.",
        });
      }
    } catch (error) {
      setFormError(error instanceof Error ? error.message : "Failed to validate API keys.");
      setValidationResult(null);
    } finally {
      setIsValidating(false);
    }
  };

  // Auto-validate Bybit API keys when both API key and secret are entered
  useEffect(() => {
    // Clear existing timeout
    if (autoValidationTimeout) {
      clearTimeout(autoValidationTimeout);
    }

    // Only validate if provider is Bybit and both fields are filled
    if (
      formState.provider === "bybit" &&
      formState.apiKey.trim().length > 0 &&
      formState.apiSecret.trim().length > 0 &&
      !isValidating
    ) {
      // Debounce: wait 1 second after user stops typing
      const timeout = setTimeout(() => {
        void handleValidateBybit();
      }, 1000);
      setAutoValidationTimeout(timeout);
    } else {
      // Reset validation result if fields are cleared
      if (
        formState.provider !== "bybit" ||
        formState.apiKey.trim().length === 0 ||
        formState.apiSecret.trim().length === 0
      ) {
        setValidationResult(null);
        setFormError(null);
      }
    }

    // Cleanup timeout on unmount or when dependencies change
    return () => {
      if (autoValidationTimeout) {
        clearTimeout(autoValidationTimeout);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formState.provider, formState.apiKey, formState.apiSecret]);

  const handleFormSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!formState.provider || !formState.apiKey || !formState.apiSecret || !formState.nickname) {
      setFormError("All fields are required.");
      return;
    }

    // For Bybit, validate first if not already validated
    if (formState.provider === "bybit") {
      // If validation is in progress, wait for it to complete
      if (isValidating) {
        setFormError("Please wait for API keys validation to complete.");
        return;
      }

      // If not validated yet, trigger validation automatically
      if (!validationResult) {
        setFormError("Validating API keys... Please wait.");
        try {
          const result = await validateBybitKeys({
            api_key: formState.apiKey,
            api_secret: formState.apiSecret,
            testnet: false,
          });
          setValidationResult(result);

          if (!result.valid) {
            if (result.withdraw_enabled || result.deposit_enabled) {
              setFormError(
                "Disable Withdraw/Deposit permissions in Bybit API settings, then try again.",
              );
            } else {
              setFormError(result.error || "API keys validation failed. Please check your credentials.");
            }
            return;
          }
        } catch (error) {
          setFormError(error instanceof Error ? error.message : "Failed to validate API keys.");
          return;
        }
      }

      if (!validationResult) {
        return;
      }

      // Check validation result
      if (!validationResult.valid) {
        setFormError(
          validationResult.error || "API keys validation failed. Please check your credentials.",
        );
        return;
      }

      // Check again for withdraw/deposit permissions
      if (validationResult.withdraw_enabled || validationResult.deposit_enabled) {
        setFormError(
          "Disable Withdraw/Deposit permissions in Bybit API settings, then try again.",
        );
        return;
      }
    }

    registerMutation.mutate(formState);
  };

  const handleRotateSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!rotationState.apiKey || !rotationState.apiSecret) {
      setRotationError("Both API key and secret are required.");
      return;
    }
    rotationMutation.mutate();
  };

  const renderAccountStatusPill = (status: IntegrationStatus) => {
    const tone: "success" | "warning" | "danger" | "neutral" =
      status === "active" ? "success" : status === "pending" ? "warning" : status === "error" ? "danger" : "neutral";
    return <Pill tone={tone}>{status}</Pill>;
  };

  /* istanbul ignore next */
  return (
    <div className="integrations-root">
      {notice ? (
        <div className={`integrations-banner integrations-banner--${notice.type}`}>
          {notice.message}
        </div>
      ) : null}
      <div className="integrations-grid">
        <Card
          className="overview-card integrations-card"
          title="Trading Accounts"
          subtitle="Each connection is unique per subscriber. API keys stay encrypted and scoped to essential permissions."
        >
          <div className="section-legend">
            <span className="section-legend__dot" />
            <div>
              Connect the exchange accounts you want the system to trade or monitor. We only request
              the scopes you select and encrypt everything immediately.
            </div>
          </div>

          {isLoading ? (
            <div className="integrations-empty">Loading trading accounts...</div>
          ) : tradingAccounts.length === 0 ? (
            <div className="integrations-empty">
              {integrationsData?.tradingAccounts?.meta?.emptyStateMessage ??
                "No trading accounts connected yet. Add one to begin trading or monitoring."}
              {showStepGuide?.step === 1 && (
                <div className="integrations-step-guide integrations-step-guide--step1">
                  <strong> Step 1:</strong> Click "Add trading account" below to create your first trading account.
                </div>
              )}
            </div>
          ) : (
            <div className="connections-list">
              {tradingAccounts.map((account) => (
                <div key={account.id} className="connections-item">
                  <div className="connections-item__info">
                    <span className="connections-item__name">{account.nickname}</span>
                    <div className="connections-item__meta">
                      Provider: {account.provider} Â· Last sync: {account.lastSyncedAt ?? "never"}
                    </div>
                  </div>
                  <div className="connections-item__actions" style={{ position: "relative" }}>
                    {renderAccountStatusPill(account.status)}
                    {account.status !== "active" ? (
                      <>
                        <div style={{ position: "relative" }}>
                          <Button
                            variant="primary"
                            size="sm"
                            disabled={activationMutation.isPending}
                            onClick={() => activationMutation.mutate(account.id)}
                          >
                            Activate
                          </Button>
                          {showStepGuide?.step === 2 && (
                            <div className="integrations-step-guide integrations-step-guide--step2 integrations-step-guide--floating">
                              <strong> Step 2:</strong> Click "Activate" to enable this account for trading.
                            </div>
                          )}
                        </div>
                      </>
                    ) : null}
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={rotationAccountId === account.id}
                      onClick={() => {
                        setRotationAccountId(account.id);
                        setRotationState({ apiKey: "", apiSecret: "" });
                        setRotationError(null);
                      }}
                    >
                      {rotationAccountId === account.id ? "Updating..." : "Update API"}
                    </Button>
                    <Button
                      variant="danger"
                      size="sm"
                      disabled={deleteAccountMutation.isPending}
                      onClick={() => deleteAccountMutation.mutate(account.id)}
                    >
                      Disable
                    </Button>
                  </div>
                  {/* Inline rotation form for this account */}
                  {rotationAccountId === account.id && (
                    <div className="integrations-inline-form">
                      <div className="integrations-inline-form__header">
                        <span className="integrations-inline-form__icon">ðŸ”‘</span>
                        <span className="integrations-inline-form__title">Update API Credentials</span>
                        <button
                          type="button"
                          className="integrations-inline-form__close"
                          onClick={() => {
                            setRotationAccountId(null);
                            setRotationState({ apiKey: "", apiSecret: "" });
                          }}
                          title="Cancel"
                        >
                          âœ•
                        </button>
                      </div>
                      <form className="integrations-form" onSubmit={handleRotateSubmit}>
                        <label>
                          New API key
                          <input
                            type="text"
                            value={rotationState.apiKey}
                            onChange={(event) => setRotationState((prev) => ({ ...prev, apiKey: event.target.value }))}
                            placeholder="Enter new API key"
                            autoFocus
                          />
                        </label>
                        <label>
                          New secret
                          <input
                            type="password"
                            value={rotationState.apiSecret}
                            onChange={(event) => setRotationState((prev) => ({ ...prev, apiSecret: event.target.value }))}
                            placeholder="Enter new API secret"
                          />
                        </label>
                        {rotationError ? <p className="integrations-error">{rotationError}</p> : null}
                        <div className="integrations-actions">
                          <Button type="submit" variant="primary" size="sm" disabled={rotationMutation.isPending}>
                            {rotationMutation.isPending ? "Updating..." : "âœ“ Save New API"}
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            disabled={rotationMutation.isPending}
                            onClick={() => {
                              setRotationAccountId(null);
                              setRotationState({ apiKey: "", apiSecret: "" });
                            }}
                          >
                            Cancel
                          </Button>
                        </div>
                      </form>
                    </div>
                  )}
                </div>
              ))}
              {tradingAccounts.some(acc => acc.status !== "active") && showStepGuide?.step === 2 && (
                <div className="integrations-step-guide integrations-step-guide--step2">
                  <strong> Next Step:</strong> Activate your account by clicking the "Activate" button above to start trading.
                </div>
              )}
            </div>
          )}

          {pendingRequests.length > 0 ? (
            <div className="integrations-alert">
              Pending requests detected. Review them to ensure accounts are verified promptly.
            </div>
          ) : null}

          <div className="integrations-actions">
            <Button
              variant="ghost"
              onClick={async () => {
                if (data?.tradingAccounts?.items) {
                  const activeAccounts = data.tradingAccounts.items.filter(
                    (account) => account.status === "active",
                  );
                  try {
                    await Promise.all(
                      activeAccounts.map((account) => refreshAccountSnapshot(account.id)),
                    );
                    await refetch();
                    setNotice({ type: "success", message: "Accounts refreshed successfully." });
                  } catch (error) {
                    setNotice({
                      type: "error",
                      message: error instanceof Error ? error.message : "Failed to refresh accounts.",
                    });
                  }
                } else {
                  await refetch();
                }
              }}
            >
              Refresh accounts
            </Button>
          </div>
        </Card>

        <Card
          className="overview-card integrations-card"
          title="Link a new account"
          subtitle="Connect your Bybit account. Bybit is currently the only supported exchange for automated trading."
        >
          {formState.provider === "bybit" && !formState.apiKey ? (
            <div className="integrations-requirements">
              <div className="integrations-requirements__title">
                Bybit API Key Requirements
              </div>

              <div className="integrations-requirements__section">
                <div className="integrations-requirements__section-title">
                  API Key Type
                </div>
                <div className="integrations-requirements__item">
                  Read-Write (not Read-Only)
                </div>
              </div>

              <div className="integrations-requirements__section">
                <div className="integrations-requirements__section-title">
                  IP Restriction
                </div>
                <div className="integrations-requirements__item">
                  No IP Restriction (or add server IPs)
                </div>
              </div>

              <div className="integrations-requirements__section">
                <div className="integrations-requirements__section-title">
                  Unified Trading (all must be enabled)
                </div>
                <div className="integrations-requirements__item integrations-requirements__item--enabled">
                  Order
                </div>
                <div className="integrations-requirements__item integrations-requirements__item--enabled">
                  Position
                </div>
                <div className="integrations-requirements__item integrations-requirements__item--enabled">
                  USDC Derivatives Trading
                </div>
                <div className="integrations-requirements__item integrations-requirements__item--enabled">
                  Trade
                </div>
              </div>

              <div className="integrations-requirements__section">
                <div className="integrations-requirements__section-title">
                  Assets / Wallet (all must be disabled)
                </div>
                <div className="integrations-requirements__item integrations-requirements__item--disabled">
                  Account Transfer
                </div>
                <div className="integrations-requirements__item integrations-requirements__item--disabled">
                  Sub Account Transfer
                </div>
                <div className="integrations-requirements__item integrations-requirements__item--disabled">
                  Withdraw
                </div>
                <div className="integrations-requirements__item integrations-requirements__item--disabled">
                  Deposit
                </div>
              </div>

              <div className="integrations-requirements__section">
                <div className="integrations-requirements__section-title">
                  Exchange (must be enabled)
                </div>
                <div className="integrations-requirements__item integrations-requirements__item--enabled">
                  Convert
                </div>
                <div className="integrations-requirements__item integrations-requirements__item--enabled">
                  Exchange History
                </div>
              </div>

              <div className="integrations-requirements__note">
                If your key is restricted to IPs, make sure your server IP is whitelisted.
                Keys with "No IP Restriction" expire in 3 months.
              </div>

              <a
                className="integrations-requirements__link"
                href="https://www.bybit.com/app/user/api-management"
                target="_blank"
                rel="noopener noreferrer"
              >
                Open Bybit API Management &rarr;
              </a>

              <div style={{ marginTop: "var(--space-sm)", fontSize: "var(--font-size-xs)", color: "rgba(148, 163, 184, 0.6)" }}>
                Enter your API key and secret below to validate automatically.
              </div>
            </div>
          ) : (
            <div className="section-legend">
              <span className="section-legend__dot" />
              <div>
                Grab your API key and secret from the exchange, make sure trading permissions are
                enabled, then paste them below. We will validate the credentials before storing them.
              </div>
            </div>
          )}
          {isAccountFormOpen ? (
            <form className="integrations-form" onSubmit={handleFormSubmit}>
              <label>
                Exchange
                <select
                  value={formState.provider}
                  onChange={(event) => {
                    setFormState((prev) => ({ ...prev, provider: event.target.value }));
                    // Reset validation when provider changes
                    setValidationResult(null);
                    setFormError(null);
                  }}
                  required
                >
                  <option value="" disabled>
                    Select exchange
                  </option>
                  <option value="bybit">Bybit</option>
                </select>
              </label>
              <label>
                API Key
                <input
                  type="text"
                  value={formState.apiKey}
                  onChange={(event) => {
                    setFormState((prev) => ({ ...prev, apiKey: event.target.value }));
                    // Reset validation when API key changes
                    setValidationResult(null);
                    setFormError(null);
                  }}
                  placeholder="Enter public API key"
                  required
                />
              </label>
              <label>
                Secret
                <input
                  type="password"
                  value={formState.apiSecret}
                  onChange={(event) => {
                    setFormState((prev) => ({ ...prev, apiSecret: event.target.value }));
                    // Reset validation when secret changes
                    setValidationResult(null);
                    setFormError(null);
                  }}
                  placeholder="â€¢â€¢â€¢â€¢â€¢â€¢â€¢â€¢"
                  required
                />
              </label>
              {formState.provider === "bybit" && formState.apiKey && formState.apiSecret && (
                <div className="integrations-validation-section">
                  {isValidating && (
                    <div className="integrations-validation-info">
                      â³ Validating API keys...
                    </div>
                  )}
                  {!isValidating && validationResult && validationResult.valid && (
                    <div className="integrations-validation-success">
                      âœ“ API keys validated successfully. Trading permissions enabled.
                    </div>
                  )}
                  {!isValidating && validationResult && !validationResult.valid && (
                    <div className="integrations-validation-error">
                      Validation failed. Your API key does not meet all requirements:
                      {validationResult.read_only && (
                        <div className="integrations-validation-error-item integrations-validation-error-item--fail">
                          Read-Only mode is enabled.
                          <span className="integrations-validation-error-item__fix">
                            Fix: Edit your API key on Bybit and set type to "Read-Write".
                          </span>
                        </div>
                      )}
                      {validationResult.withdraw_enabled && (
                        <div className="integrations-validation-error-item integrations-validation-error-item--fail">
                          Withdraw permission is enabled (must be disabled).
                          <span className="integrations-validation-error-item__fix">
                            Fix: Under "Assets" section, uncheck "Withdraw".
                          </span>
                        </div>
                      )}
                      {validationResult.deposit_enabled && (
                        <div className="integrations-validation-error-item integrations-validation-error-item--fail">
                          Deposit permission is enabled (must be disabled).
                          <span className="integrations-validation-error-item__fix">
                            Fix: Under "Assets" section, uncheck "Deposit".
                          </span>
                        </div>
                      )}
                      {validationResult.missing_unified_permissions &&
                        validationResult.missing_unified_permissions.length > 0 && (
                          <div className="integrations-validation-error-item integrations-validation-error-item--fail">
                            Missing Unified Trading permissions:{" "}
                            {validationResult.missing_unified_permissions.join(", ")}.
                            <span className="integrations-validation-error-item__fix">
                              Fix: Under "Unified Trading" section, enable all (Order, Position, USDC Derivatives Trading, Trade).
                            </span>
                          </div>
                        )}
                      {validationResult.missing_exchange_permissions &&
                        validationResult.missing_exchange_permissions.length > 0 && (
                          <div className="integrations-validation-error-item integrations-validation-error-item--fail">
                            Missing Exchange permissions:{" "}
                            {validationResult.missing_exchange_permissions.join(", ")}.
                            <span className="integrations-validation-error-item__fix">
                              Fix: Under "Exchange" section, enable "Convert" and "Exchange History".
                            </span>
                          </div>
                        )}
                      <div style={{ marginTop: "var(--space-xs)", padding: "var(--space-xs)", fontSize: "0.75em", color: "rgba(203, 213, 225, 0.6)" }}>
                        How to fix:
                        Go to{" "}
                        <a
                          href="https://www.bybit.com/app/user/api-management"
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ color: "var(--color-info-400)" }}
                        >
                          Bybit API Management
                        </a>
                        , click "Edit" on your API key, apply the changes above, click "Submit", and enter your 2FA code. Then come back and try again.
                      </div>
                    </div>
                  )}
                  {!isValidating && !validationResult && (
                    <div className="integrations-validation-info">
                      â„¹ï¸ Enter API key and secret to validate automatically
                    </div>
                  )}
                </div>
              )}
              <label>
                Nickname
                <input
                  type="text"
                  value={formState.nickname}
                  onChange={(event) => setFormState((prev) => ({ ...prev, nickname: event.target.value }))}
                  placeholder="e.g. Binance main"
                  required
                />
              </label>
              <label>
                Owner (optional)
                <input
                  type="text"
                  value={formState.owner ?? ""}
                  onChange={(event) => setFormState((prev) => ({ ...prev, owner: event.target.value }))}
                  placeholder="team or user"
                />
              </label>
              <label
                className="integrations-consent"
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: "0.5rem",
                  fontSize: "0.85rem",
                  margin: "1rem 0",
                  cursor: "pointer",
                }}
              >
                <input
                  type="checkbox"
                  checked={consentAccepted}
                  onChange={(event) => setConsentAccepted(event.target.checked)}
                  style={{ marginTop: "0.2rem" }}
                />
                <span>
                  I understand that connecting this API key (Trade-only, no Withdraw/Deposit) authorizes
                  automated trading on my account, and that I may disable trading or revoke this key at any
                  time. I agree to the{" "}
                  <a href="/terms" target="_blank" rel="noopener noreferrer">Terms of Service</a> and{" "}
                  <a href="/risk-disclaimer" target="_blank" rel="noopener noreferrer">Risk Disclaimer</a>.
                </span>
              </label>
              {formError ? <p className="integrations-error">{formError}</p> : null}
              {showStepGuide?.step === 1 && (
                <div className="integrations-step-guide integrations-step-guide--step1">
                  <strong> Step 1:</strong> Fill in all fields above, then click "Connect account" below to create your trading account.
                </div>
              )}
              <div className="integrations-actions">
                <Button
                  type="submit"
                  variant="primary"
                  disabled={
                    isSubmitting ||
                    !consentAccepted ||
                    (formState.provider === "bybit" &&
                      (!validationResult || !validationResult.valid))
                  }
                >
                  {isSubmitting ? "Connecting..." : "Connect account"}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => {
                    setAccountFormOpen(false);
                    setFormError(null);
                    setValidationResult(null);
                    setIsValidating(false);
                    setConsentAccepted(false);
                    setFormState({
                      provider: "",
                      apiKey: "",
                      apiSecret: "",
                      nickname: "",
                      owner: "",
                      scopes: [],
                      metadata: {},
                    });
                  }}
                >
                  Cancel
                </Button>
              </div>
              <p className="integrations-hint">
                Keys are encrypted at rest. You can revoke access at any time from the trading platform.
              </p>
            </form>
          ) : (
            <div className="integrations-actions">
              {tradingAccounts.length === 0 || isAdmin ? (
                <Button
                  variant="primary"
                  onClick={() => {
                    setAccountFormOpen(true);
                    setFormError(null);
                    // Show step guide for first step
                    if (!showStepGuide) {
                      setShowStepGuide({
                        step: 1,
                        message: "Fill in your API credentials below, then click 'Connect account' to create your trading account.",
                      });
                    }
                  }}
                >
                  Add trading account
                </Button>
              ) : (
                <div className="integrations-info">
                  You already have a trading account connected. Use "Update API" to change your API credentials, or "Disable" to remove the current account.
                </div>
              )}
              {showStepGuide?.step === 1 && (
                <div className="integrations-step-guide integrations-step-guide--step1">
                  <strong> Step 1:</strong> {showStepGuide.message}
                </div>
              )}
            </div>
          )}
        </Card>
      </div>

      {!isAdmin && (
        <Card
          className="overview-card integrations-card"
          title="Telegram Notifications"
          subtitle="Link your Telegram account to receive personalized notifications"
        >
          {telegramStatus?.is_linked ? (
            <div className="connections-list">
              <div className="connections-item">
                <div className="connections-item__info">
                  <span className="connections-item__name">Telegram Connected</span>
                  <div className="connections-item__meta">
                    Chat ID: {telegramStatus.chat_id}
                  </div>
                </div>
                <div className="connections-item__actions">
                  <Button
                    variant="danger"
                    size="sm"
                    disabled={unlinkTelegramMutation.isPending}
                    onClick={() => {
                      if (window.confirm("Are you sure you want to unlink your Telegram account?")) {
                        unlinkTelegramMutation.mutate();
                      }
                    }}
                  >
                    {unlinkTelegramMutation.isPending ? "Unlinking..." : "Unlink"}
                  </Button>
                </div>
              </div>
            </div>
          ) : (
            <div className="integrations-empty">
              {telegramStatus?.bot_link ? (
                <div>
                  <p>Click the button below to open Telegram and start the bot:</p>
                  <div style={{ marginTop: "16px" }}>
                    <Button
                      variant="primary"
                      onClick={() => window.open(buildTelegramDeepLink(telegramStatus.bot_link)!, "_blank")}
                    >
                      Open Telegram Bot
                    </Button>
                  </div>
                  <p style={{ marginTop: "16px", fontSize: "0.875rem", color: "var(--color-text-secondary)" }}>
                    Once you open the bot, click <strong>Start</strong> to link your account.
                  </p>
                </div>
              ) : (
                <p>Telegram bot is not configured. Please contact the administrator.</p>
              )}
            </div>
          )}
        </Card>
      )}

      <AdminBroadcastCard isAdmin={isAdmin} />

      <AdminEmailSettingsCard isAdmin={isAdmin} />

      <AdminScheduledReportsCard isAdmin={isAdmin} />
      <Card className="overview-card integrations-card" title="Pending requests">
        {pendingRequests.length === 0 ? (
          <div className="integrations-empty">
            {integrationsData?.pendingRequests?.meta?.emptyStateMessage ??
              "No pending account submissions. Fill the form to request a new connection."}
          </div>
        ) : (
          <div className="connections-list">
            {pendingRequests.map((request, index) => (
              <div key={`${request.exchange}-${request.nickname}-${index}`} className="connections-item">
                <div className="connections-item__info">
                  <span className="connections-item__name">{request.exchange}</span>
                  <div className="connections-item__meta">
                    Status: {request.status} Â· Submitted: {request.submittedAt ?? "n/a"}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      <div className="integrations-footer">
        <Button variant="ghost" onClick={() => refetch()}>
          Refresh data
        </Button>
        <div className="integrations-summary">
          <strong>Summary:</strong> {integrationsData?.summary.message ?? "No integrations configured yet."}
        </div>
      </div>

      <BybitOnboardingModal
        isOpen={isOnboardingModalOpen}
        onClose={() => {
          setIsOnboardingModalOpen(false);
          setAccountFormOpen(true);
        }}
        onContinue={() => setAccountFormOpen(true)}
      />
    </div>
  );
}
